/*
 * Webmail's client for /api/webmail/. Every failure is an ApiError whose
 * message is safe to show as it is.
 */
import { ApiError } from "../api/client";
import type { Upload } from "./types";

export { ApiError };

const BASE = "/api/webmail";

type Method = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

let onSignedOut: ((mailboxId: string | null) => void) | null = null;
/** The app registers this: a 401 means this mailbox (or the whole session) needs signing in again. */
export function setSignedOutHandler(fn: (mailboxId: string | null) => void) {
  onSignedOut = fn;
}

/** For the live event stream: this mailbox needs signing in again. */
export function reportSignedOut(mailboxId: string | null) {
  onSignedOut?.(mailboxId);
}

async function parse(res: Response): Promise<unknown> {
  const text = await res.text();
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = null;
    }
  }
  if (!res.ok) {
    const e = (json as { error?: { code?: string; message?: string; fields?: Record<string, string> } } | null)?.error;
    if (e?.message) throw new ApiError(res.status, e.code ?? "error", e.message, e.fields ?? {});
    if (res.status >= 500 && (res.status === 502 || res.status === 503 || res.status === 504 || json === null)) {
      throw new ApiError(res.status, "unavailable", "Webmail isn't responding right now. Try again in a moment.");
    }
    throw new ApiError(res.status, "error", "Something went wrong. Try again in a moment.");
  }
  return json;
}

async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  const init: RequestInit = { method, credentials: "same-origin", headers };
  if (method !== "GET") {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body ?? {});
  }
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, init);
  } catch {
    throw new ApiError(0, "network", "We can't reach webmail. Check your connection and try again.");
  }
  try {
    return (await parse(res)) as T;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401 && (err.code === "signed_out" || err.code === "mail_auth")) {
      const m = /^\/m\/([0-9a-f-]{36})\//.exec(path);
      onSignedOut?.(m ? m[1]! : null);
    }
    throw err;
  }
}

export const wmApi = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  del: <T>(path: string, body?: unknown) => request<T>("DELETE", path, body),
};

/** Uploads a file for compose, reporting progress. */
export function uploadFile(mailboxId: string, file: File, onProgress: (fraction: number) => void, signal?: AbortSignal): Promise<Upload> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${BASE}/m/${mailboxId}/uploads?name=${encodeURIComponent(file.name || "attachment")}`);
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.setRequestHeader("X-Upload-Type", file.type || "application/octet-stream");
    xhr.setRequestHeader("Accept", "application/json");
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onerror = () => reject(new ApiError(0, "network", "The upload didn't reach webmail. Check your connection and try again."));
    xhr.onabort = () => reject(new DOMException("aborted", "AbortError"));
    xhr.onload = () => {
      parse(new Response(xhr.responseText, { status: xhr.status }))
        .then((j) => resolve((j as { upload: Upload }).upload))
        .catch(reject);
    };
    signal?.addEventListener("abort", () => xhr.abort());
    xhr.send(file);
  });
}

/** An attachment as a blob, for the in-app viewer (raster images and PDF only). */
export async function fetchAttachment(mailboxId: string, folder: string, uid: number, part: string): Promise<Blob> {
  const res = await fetch(`${BASE}/m/${mailboxId}/attachment?folder=${encodeURIComponent(folder)}&uid=${uid}&part=${encodeURIComponent(part)}`, { credentials: "same-origin" });
  if (!res.ok) await parse(res);
  return res.blob();
}

export const attachmentUrl = (mailboxId: string, folder: string, uid: number, part: string) =>
  `${BASE}/m/${mailboxId}/attachment?folder=${encodeURIComponent(folder)}&uid=${uid}&part=${encodeURIComponent(part)}`;

/** The message as it arrived: "Show original" (plain text, in a tab of its own) or, with `download`, a .eml file. */
export const rawMessageUrl = (mailboxId: string, folder: string, uid: number, download = false) =>
  `${BASE}/m/${mailboxId}/raw?folder=${encodeURIComponent(folder)}&uid=${uid}${download ? "&download=1" : ""}`;

/** The mailbox as an iPhone, iPad and Mac Mail profile, without its password. */
export const mobileconfigUrl = (mailboxId: string) => `${BASE}/m/${mailboxId}/mobileconfig`;

export const qs = (params: Record<string, string | number | boolean | null | undefined>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "" && v !== false) s.set(k, String(v === true ? 1 : v));
  const out = s.toString();
  return out ? `?${out}` : "";
};
